// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Assignees, DdjDocument, DocumentStatus, FormDocumentUploadType, InternalDocumentTypeIds } from '@interfaces/document.interfaces';
import { catchError, takeWhile } from 'rxjs/operators';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { AppEvent } from '@interfaces/interfaces';
import { DealershipService } from '@services/dealership.service';
import { UserService } from '@services/user.service';
import { Deal } from '@interfaces/deal.interfaces';
import { User } from '@interfaces/user.interfaces';
import { firstValueFrom } from 'rxjs';
import { formDataToJson } from 'src/app/utils/formDataToJson';
import { isIdentification } from 'src/app/utils/document-utils';
import { MatDialog } from '@angular/material/dialog';
import { FileSizeErrorModalComponent } from 'src/app/modals/file-size-error-modal/file-size-error-modal.component';
import { isMobileOrTablet } from 'src/app/utils/utils';
import { VendorService } from '@services/vendor.service';

@Component({
    selector: 'app-buyer-upload',
    templateUrl: './buyer-upload.component.html',
    styleUrls: ['./buyer-upload.component.scss'],
})
export class BuyerUploadComponent implements OnInit, OnDestroy {
    public deal: Deal = null;
    public user: User;
    public dealId: number;
    private dealerId: number;
    public docId: number;
    public document: DdjDocument;
    public assignee: Assignees;
    public fileName = 'Select a file';
    public fileNameToSave: string;
    private isComponentAlive = true;
    public isLoading = false;
    public isPdfShowing = false;
    public template = 'upload';
    public uploadFile: any = null;
    public internalDocumentTypeId: InternalDocumentTypeIds = null;
    public jacketType: 'deals' | 'documents' = 'deals';

    constructor(
        private dealershipService: DealershipService,
        private readonly documentsService: DocumentsService,
        private readonly errorService: ErrorService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly vendorService: VendorService,
        private readonly dialog: MatDialog
    ) {
        this.route.params.pipe(takeWhile(() => this.isComponentAlive)).subscribe((param) => {
            this.jacketType = window.location.href.includes('/documents/') ? 'documents' : 'deals';

            this.docId = parseInt(param.docId);
            this.document = this.documentsService.getDocById(this.docId);
            this.dealId = this.document?.dealId;

            //if by some error or user typing in the url we end up here on an indentification doc
            //redirect to the correct upload component
            if (isIdentification(this.document.internalDocumentTypeId)) {
                if (isMobileOrTablet()) {
                    this.router.navigate(['/main', 'vehicles', this.dealId, this.docId, 'uploadIdn']);
                } else {
                    this.router.navigate(['/main', 'vehicles', this.dealId]);
                }
            }

            // refresh the file upload in case dealId or docId updates
            this.isPdfShowing = false;
            this.uploadFile = undefined;
            this.fileName = undefined;

            //get the dealerId for any document should we not have a document here
            //for instance changing the url to something not valid
            this.dealerId = this.document?.dealerId;
            if (!this.document || this.document === undefined) {
                this.dealerId = this.documentsService.documents[0].dealerId;
            }

            this.userService.setUserCurrentDealer(this.dealerId);
        });

        this.dealershipService
            .getDealer$(this.document?.dealerId ?? this.dealerId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    console.error('unable to get dealer', error);
                    throw error;
                })
            )
            .subscribe();
    }

    ngOnInit(): void {
        const data = this.route.snapshot.data;
        this.user = data.user;
        this.assignee = this.deal?.coBuyer?.userId === this.user.userId ? Assignees.cobuyer : Assignees.buyer;

        data.deal.pipe(takeWhile(() => this.isComponentAlive)).subscribe((deal) => {
            this.deal = deal;
        });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    public cancelUpload(): void {
        this.router.navigate(['/main', 'vehicles', this.dealId]);
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'cancel':
                this.cancelUpload();
                break;
            case 'image-uploaded':
                this.saveImage(event.data);
                break;
            default:
                break;
        }
    }

    private async saveImage(data: any): Promise<void> {
        this.isLoading = true;
        this.documentsService
            .uploadDocumentFileV2$(this.dealId, this.docId, formDataToJson(data.formData))
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((doc) => {
                this.isLoading = false;
                const isCustomError = this.checkForCustomError(doc);
                if (!isCustomError && doc && doc.documentId) {
                    this.successfulUpload();
                }
            });
    }

    private checkForCustomError(response): boolean {
        if (response.error && response.statusCode == 550) {
            this.errorService.showError({
                message: response.message,
                statusCode: 550,
            });
            return true;
        }
        return false;
    }

    get isIdentification(): boolean {
        return isIdentification(this.internalDocumentTypeId);
    }

    public selectPdf(file: any): void {
        const fileSizeInMegabytes = file.size / 1048576;

        if (fileSizeInMegabytes >= 25) {
            this.dialog.open(FileSizeErrorModalComponent, {
                width: '700px',
                closeOnNavigation: false,
                autoFocus: true,
                data: {
                    isMultipleUpload: false,
                },
            });
        } else {
            if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type.includes('pdf')) {
                if (file.name.length > 100) {
                    this.errorService.showError('The file name cannot exceed 100 characters.');
                    return;
                }
                this.isPdfShowing = true;
                this.uploadFile = file;
                this.fileName = file.name;
                this.internalDocumentTypeId = this.document.internalDocumentTypeId;

                this.fileNameToSave = this.document.displayName.replace(/[^a-zA-Z0-9 _.-]/g, '');
            } else {
                this.errorService.showError('The file uploaded must be a jpg, jpeg or pdf.');
            }
        }
    }

    public async getAllDocs() {
        const docs = await firstValueFrom(this.documentsService.getDocsList$(this.deal.dealId));
        return docs.filter(
            (doc) =>
                (doc.documentStatusId === DocumentStatus.DealerQueue ||
                    doc.documentStatusId === DocumentStatus.BuyerQueue ||
                    doc.documentStatusId === DocumentStatus.CoBuyerQueue) &&
                (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
        );
    }

    public getCustomerDocs(docs: DdjDocument[]) {
        const isBuyer = this.deal.buyer.userId === this.user.userId;

        if (isBuyer) {
            return docs.filter(
                (doc) =>
                    doc.documentStatusId === DocumentStatus.BuyerQueue &&
                    (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
            );
        } else {
            return docs.filter(
                (doc) =>
                    doc.documentStatusId === DocumentStatus.CoBuyerQueue &&
                    (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
            );
        }
    }

    private async successfulUpload() {
        let routeToGo = ['/main', 'vehicles', this.deal.dealId];

        if (this.deal && this.deal.dealDocs) {
            // Get all documents to check if all documents have been completed
            let docs = await this.getAllDocs();
            const finalizeDocumentsEnabled = this.documentsService.finalizeEnabledForCustomer(this.assignee);

            // Since we enable finalize for customers before all documents are completed,
            // we are no longer going to try and close the deal when customer completes all their documents

            docs = this.getCustomerDocs(docs) ?? [];
            // Do not go to finalize docs if the user is not ready to yet
            if (!finalizeDocumentsEnabled) {
                // Docs !== BuyerFinalSignatureDocument(25) AND CoBuyerFinalSignatureDocument(34)
                docs = docs.filter((d) => d.internalDocumentTypeId !== 25 && d.internalDocumentTypeId !== 34);
            }

            //do not move to an identification doc if we're not on mobile
            if (!isMobileOrTablet()) {
                docs = docs.filter((d) => !isIdentification(d.internalDocumentTypeId));
            }

            if (docs.length > 0) {
                const nextDoc = docs[0];

                if (nextDoc?.fileName || this.vendorService.isDocumentForRouteOne(nextDoc)) {
                    routeToGo = ['/main', 'vehicles', nextDoc.dealId, nextDoc.documentId, 'sign'];
                } else {
                    const url = isIdentification(nextDoc.internalDocumentTypeId) ? 'uploadIdn' : 'upload';
                    routeToGo = ['/main', 'vehicles', nextDoc.dealId, nextDoc.documentId, url];
                }
            }
        }

        this.router.navigate(routeToGo);
    }
}
